import {Component, DestroyRef, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {ModalComponent} from '../../../../shared/components/modal/modal.component';
import {ToastrService} from 'ngx-toastr';
import {RuleCheckerService} from '../../../../services/store/rule-checker.service';
import {EmployeeManagerSettingsService} from '../../../../services/http/employee/employee-manager-settings.service';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";

/** Data required to open the manager settings delete modal. */
export interface ManagerSettingsDeleteData {
  employeePositionId?: EmployeePositionId;
  date: string;
}

/**
 * This component handles deleting manager settings.
 */
@Component({
  selector: 'app-manager-settings-delete',
  templateUrl: './manager-settings-delete.component.html',
  styleUrls: ['./manager-settings-delete.component.less'],
  imports: [SHARED_IMPORTS]
})
export class ManagerSettingsDeleteComponent {
  private readonly destroyRef = inject(DestroyRef);
  private employeeManagerSettingsService = inject(EmployeeManagerSettingsService);
  private toastr = inject(ToastrService);
  private ruleCheckerService = inject(RuleCheckerService);

  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId | undefined>(undefined);
  /** A date from which manager settings could be deleted. */
  readonly date = signal<string>('');

  /** `True` if a request to backend is in progress. */
  loading = false;

  static open(modalService: NgbModal, data: ManagerSettingsDeleteData): NgbModalRef {
    const modalRef = modalService.open(ManagerSettingsDeleteComponent);
    const instance = modalRef.componentInstance as ManagerSettingsDeleteComponent;
    instance.employeePositionId.set(data.employeePositionId);
    instance.date.set(data.date);
    return modalRef;
  }

  /**
   * Sends request to backend to delete manager settings.
   * @param modalComponent a modal component of manager settings deletion.
   */
  confirm(modalComponent: ModalComponent) {
    this.loading = true;

    this.employeeManagerSettingsService.deleteManagerSettings(this.employeePositionId(), this.date())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                modalComponent.activeModal.close();
                this.loading = false;
                this.toastr.success('Manager settings has been successfully updated');
                this.ruleCheckerService.check();
              },
              error: (error) => {
                this.loading = false;
                this.toastr.error(error.error.message);
              }
            });
  }
}